<template>
  <div v-if="task" class="progress-rows scrollbar">
    <template v-if="!singleBar && stages && stages.length > 0">
      <div v-for="(value, i) in stages" :key="i" class="progress-row">
        <div class="progress-row__value-row">
          <span>{{ value.label ? value.label : value.stage }}</span>
          <label v-if="value.percentage">{{ `${Math.round(value.percentage)}%` }}</label>
          <label v-else-if="value.count && value.total">{{ value.total > 0 && !isNaN(value.count/value.total) ? `${Math.round(value.count*100/value.total)}%` : '0%' }}</label>
        </div>
        <progress
          :value="getProgress(value)"
          :class="{ '_error' : value.status.toLowerCase().includes('error')}"
          :max="100"
        />
        <span
          v-if="value.message"
          class="progress-row__message"
          :title="value.message"
          :class="{ '_error' : value.status.toLowerCase().includes('error')}"
        >{{ value.message }}</span>
      </div>
    </template>
    <template v-else-if="singleBar && currentStage.stage && stage_definitions && stage_definitions.length > 0">
      <div class="progress-row">
        <div class="progress-row__value-row">
          <div class="d-flex">
            <SVGIcon
              v-if="showIcon"
              :iconName="getIcon(task.type)"
              :width="'20px'"
              :height="'20px'"
              class="primary-icon-color mr-1"
            />
            <span>{{ task.name }}</span>
          </div>
          <label v-if="task.status.toLowerCase().includes('complete')">Completed</label>
          <label v-else-if="currentStage.stage.percentage">{{ `${Math.round(currentStage.stage.percentage)}%` }}</label>
          <label v-else-if="currentStage.stage.count && currentStage.stage.total">
            {{ currentStage.stage.total > 0 && !isNaN(currentStage.stage.count / currentStage.stage.total)
              ? `${Math.round(currentStage.stage.count * 100/currentStage.stage.total)}%` : '0%' }}
          </label>
        </div>
        <progress
          :value="task.status.toLowerCase().includes('complete') ? 100 : getProgress(currentStage.stage)"
          :class="{ '_error' : currentStage.stage.status.toLowerCase().includes('error')}"
          :max="100"
        />
        <div
          v-if="currentStage.stage.label"
          class="progress-row__message"
          :title="currentStage.stage.label"
          :class="{ '_error' : currentStage.stage.status.toLowerCase().includes('error')}"
        >
          <span>({{ currentStage.stageIndex + 1 }}/{{ serverInit ? stage_definitions.length + 1 : stage_definitions.length }}) {{ currentStage.stage.label }}</span>
          <span v-if="showDate">{{ formatTime(task.date) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  toRefs, ref, watch, onMounted, computed,
} from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';

const props = defineProps({
  task: {
    type: Object,
    default: () => {},
  },
  singleBar: {
    type: Boolean,
    default: false,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  showDate: {
    type: Boolean,
    default: false,
  },
});

const {
  task,
} = toRefs(props);

const stages = ref([]);
const stage_definitions = ref([]);
const serverInit = ref(false);

// hide the bars if 1 of 3 conditions are met:
// 1. The overall status is complete
// 2. There are no bars
// 3. The last stage status is complete

const hideBars = computed(() => (task.value && task.value.status.toLowerCase().includes('complete'))
                              || (stages.value && stages.value.length === 0)
                              || (stages.value && stages.value.length > 0 && stages.value[stages.value.length - 1]?.status.toLowerCase().includes("complete")));

const currentStage = computed(() => {
  let stage = null;
  let stageIndex = 0;
  for (let i = stages.value.length - 1; i >= 0; i--) {
    if (stages.value[i].percentage !== 0 || (stages.value[i].count !== 0 && stages.value[i].total !== 0)) {
      stage = stages.value[i];
      stageIndex = i;
      break;
    }
  }
  if (!stage && stages.value.length === 1) {
    stage = stages.value[0];
    stageIndex = 0;
  }
  return { stage, stageIndex };
});
// (task.value && task.value.status.toLowerCase().includes('complete'))
//                               || (stages.value && stages.value.length === 0)
//                               || (stages.value && stages.value.length > 0 && stages.value[stages.value.length - 1]?.status.toLowerCase().includes("complete"))

const emit = defineEmits(['hide-bars']);

watch(hideBars, () => {
  emit('hide-bars', hideBars.value);
});

watch(task, () => {
  if (task.value) {
    parseData(task.value.data);
  }
}, { deep: true });

function getProgress(v) {
  if (v.percentage) {
    return Math.round(v.percentage);
  } else if (v.count && v.total && !Number.isNaN(v.count / v.total)) {
    return Math.round((v.count * 100) / v.total);
  } else {
    return 0;
  }
}

function parseData(data) {
  if (data === undefined) {
    stages.value = [];
  }
  let dataJson = {};
  if (typeof data === 'string') {
    if (data !== "") {
      dataJson = JSON.parse(data);
    } else {
      dataJson = {};
    }
  } else if (typeof data === 'object') {
    dataJson = data;
  }

  let percentage = 0;
  if (task.value) {
    let timer = 0;
    if (task.value.total === 0) {
      timer = 4 * 60 * 1000;
    } else {
      timer = task.value.total;
    }
    let timeDiff = (new Date()) - (new Date(task.value.date));
    if (timeDiff / timer > 1) {
      timeDiff = timer;
    }
    percentage = Math.round((timeDiff * 100) / timer);
  }
  let stages_json = null;
  let stage_definitions_keys = [];

  if (Object.keys(dataJson).length > 0) {
    stages_json = dataJson["stages"];
    stage_definitions.value = dataJson["stage_definitions"];
  }
  if (stage_definitions.value) {
    stage_definitions_keys = stage_definitions.value.map((item) => Object.keys(item)[0]);
  }

  const result = [];

  if (task.value.cloud_instance_id !== "") {
    serverInit.value = true;
    result.push({
      stage: "server-init",
      label: "Server Initialization",
      percentage,
      message: "",
      order: 0,
      status: "initializing",
    });
  }
  // Display all stages in definition
  if (stage_definitions.value) {
    stage_definitions.value.forEach((def, i) => {
      const [key, value] = Object.entries(def)[0];
      if (stages_json && key in stages_json) {
        result.push({
          stage: key,
          label: value,
          message: stages_json[key]["message"],
          percentage: stages_json[key]["percentage"],
          count: stages_json[key]["count"],
          total: stages_json[key]["total"],
          status: stages_json[key]["status"],
        });
      } else {
        result.push({
          stage: key,
          label: value,
          message: "",
          percentage: 0,
          count: 0,
          total: 100,
          status: key,
        });
      }
    });
  }

  // this block should only add if the stage is not in definition
  if (stages_json) {
    for (const [key, value] of Object.entries(stages_json)) {
      if (!key.includes('init') && !stage_definitions_keys.includes(key)) {
        if (!stage_definitions_keys.includes(key)) {
          const newObj = {
            stage: key,
            label: value["label"],
            message: value["message"],
            percentage: value["percentage"],
            count: value["count"],
            total: value["total"],
            order: value["order"],
            status: key,
          };

          result.push(newObj);
        }
      }
    }
  }

  // if the second stage has begun, remove 'server-init' stage
  if (result && result.length > 0 && task.value.cloud_instance_id !== "") {
    if (result[1] && ((result[1]["percentage"] && result[1]["percentage"] > 0)
    || (result[1]["count"] && result[1]["count"] > 0))) {
      const removeIndex = result.findIndex((e) => e.stage === "server-init");
      if (removeIndex > -1) {
        serverInit.value = false;
        result.splice(removeIndex, 1);
      }
    }
  }

  stages.value = result;
}

function formatTime(str) {
  return `${new Date(str).toLocaleDateString('default', {
    year: 'numeric', month: 'short', day: 'numeric',
  })}`;
}

function getIcon(type) {
  switch (type) {
  case 'trainer':
    return 'train';
  case 'validation':
    return 'compare';
  case 'snapshot-create':
    return 'database_export';
  case 'snapshot-restore':
    return 'database_import';
  case 'copyds':
    return 'copy_white';
  case 'upload':
    return 'cloud_upload';
  default:
    return '';
  }
}

onMounted(() => {
  if (task.value) {
    parseData(task.value.data);
  }
  emit('hide-bars', hideBars.value);
});

</script>

<style lang="scss" scoped>
.progress-rows {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: hidden;
  padding: 5px;

  .progress-row {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    width: 100%;
    height: fit-content;
    font-size: small;

    &__value-row {
      display: flex;
      flex-direction: row;
      gap: 6px;
      align-items: baseline;
      justify-content: space-between;

      span {
        font-size: 0.9rem;
        font-weight: 700;
      }

      label {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--body-text-color-secondary);
      }
    }

    &__message {
      display: flex;
      justify-content: space-between;
      flex: 1;
      font-size: 0.75rem;
      font-weight: 400;
      color: var(--body-text-color-secondary);

      &._error {
        color: var(--color-error);
      }
    }

    progress {
      border-radius: 10px;
      width: 100%;
      height: 8px;
      padding: 1px;
    }

    progress::-webkit-progress-value {
      background-color: var(--progress-value);
      border-radius: 10px;
    }

    progress._error::-webkit-progress-value {
      background-color: var(--color-error) !important;
    }

    progress::-moz-progress-bar {
      background-color: var(--progress-bar);
    }

    progress::-webkit-progress-bar {
      background-color: var(--progress-bar);
    }

  }

  .progress-row + .progress-row {
    margin-top: 20px;
  }
}

.primary-icon-color {
  color: var(--color-primary);
}
</style>
